import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  CodecraftAnswer,
  CodecraftQuestion,
  CodecraftResult,
} from '@codecraft-works/data-models';
import { Observable, combineLatest, map, switchMap } from 'rxjs';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { QuizService } from '../data-access/quiz.service';
import { CodecraftQuizReviewPageFormComponent } from './review-page-form.component';

@Component({
  standalone: true,
  selector: 'codecraft-quiz-review-page-wrapper',
  template: `
    <ng-container
      *ngIf="questionAndAnswer$ | async as questionAndAnswer; else loading"
    >
      <codecraft-quiz-review-page-form
        [question]="questionAndAnswer.question"
        [answerGiven]="questionAndAnswer.answerGiven"
        [totalQuestions]="questionAndAnswer.totalQuestions"
        [currentQuestionIndex]="questionAndAnswer.currentQuestionIndex"
        [isCorrect]="questionAndAnswer.isCorrect"
        [answer]="questionAndAnswer.answer"
      ></codecraft-quiz-review-page-form>
    </ng-container>
    <ng-template #loading>
      <codecraft-loading-spinner></codecraft-loading-spinner>
    </ng-template>
  `,
  styles: [],
  imports: [
    NgIf,
    AsyncPipe,
    CodecraftQuizReviewPageFormComponent,
    LoadingSpinnerComponent,
  ],
})
export class CodecraftQuizReviewPageWrapperComponent implements OnInit {
  questionAndAnswer$: Observable<{
    question: CodecraftQuestion;
    answer: CodecraftAnswer;
    answerGiven: CodecraftAnswer;
    currentQuestionIndex: number;
    totalQuestions: number;
    isCorrect: boolean;
  }>;

  constructor(private quizService: QuizService) {}

  ngOnInit(): void {
    try {
      this.questionAndAnswer$ = combineLatest([
        this.quizService.getQuizInstance$(),
        this.quizService.getQuizReviewState$(),
      ]).pipe(
        switchMap(async ([quizInstance, quizReviewState]) => {
          if (
            !quizReviewState ||
            !quizReviewState.quizId ||
            !quizReviewState.questionId ||
            !quizInstance
          ) {
            return null;
          }

          const resultId = quizInstance.questions[quizReviewState.questionId];

          try {
            const questionResult: CodecraftResult =
              await this.quizService.getQuestionResult(resultId);
            const question = questionResult.question;
            const answer = await this.quizService
              .getQuestion(questionResult.question.id)
              .then((doc) => {
                return doc.answer as CodecraftAnswer;
              });
            const answerGiven = questionResult.answerGiven;
            const questionIds = Object.keys(quizInstance.questions);
            const currentQuestionIndex = questionIds.indexOf(
              quizReviewState.questionId
            );
            const isCorrect = questionResult.correct;
            const totalQuestions = questionIds.length;

            if (!question) {
              this.quizService.setQuizReviewState({
                component: 'error',
                error: 'Invalid question from quiz instance',
              });
              return null;
            }

            return {
              question,
              answer,
              answerGiven,
              currentQuestionIndex,
              totalQuestions,
              isCorrect,
            };
          } catch (error) {
            console.error('API Error:', error);
            return null;
          }
        })
      );
    } catch (error) {
      this.quizService.setQuizReviewState({
        component: 'error',
        error: 'Question load error: ' + error,
      });
    }
  }
}
